export const dynamic = 'force-dynamic';
import { FC, Fragment } from 'react';
import { Text, View } from '@react-pdf/renderer';

import { IWBeneficiary, useIwEditingStore } from '@/entities/interwills';
import { getCountryName } from '@/shared/constants/countries-list';
import { getUppercaseName } from '@/shared/helpers/names';
import { dates } from '@/shared/utils/dates';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import { useGetDocumentCategory, useWithPlaceholder } from './hooks/';
import { getResidenceAddress } from './helpers';

import { styles } from './styles';

export const DisplayBeneficiary: FC<{
  beneficiary: IWBeneficiary;
  block: 'witnessBlock' | 'beneficiariesBlock' | 'contingentBeneficiariesBlockBlock';
  listNumber: string;
  index: number;
}> = ({ beneficiary, block, listNumber, index }) => {
  const { t } = useTranslation('widgets/pdfs', {
    keyPrefix: 'lastWill',
  });

  const { spouse } = useIwEditingStore.getState();

  let counter = 1;
  const withPlaceholder = useWithPlaceholder();
  const getDocumentCategory = useGetDocumentCategory();

  return (
    <View style={styles.beneficiaryBlock}>
      <View style={styles.row} wrap={false}>
        <View style={styles.listNumber}>
          <Text>{listNumber}</Text>
        </View>
        <Text style={styles.subTitle}>
          {t(`${block}.subtitle`)} {block !== 'contingentBeneficiariesBlockBlock' ? index + 1 : ''}
        </Text>
      </View>

      <View style={{ ...styles.row, paddingLeft: 36 }} wrap={false}>
        <Text>
          {withPlaceholder(getUppercaseName(beneficiary), 'fullName')},{' '}
          {t('witnessBlock.dateOfBirth')}{' '}
          {withPlaceholder(
            beneficiary.birthDate && dates.formatLong(beneficiary.birthDate),
            'date',
          )}
          , {t('witnessBlock.gender')}{' '}
          {withPlaceholder(
            beneficiary.gender ? t(`genders.${beneficiary.gender}`) : undefined,
            'gender',
          )}
          .
        </Text>
      </View>

      {beneficiary.primaryCitizenship && (
        <View style={{ ...styles.row, paddingLeft: 36 }} wrap={false}>
          <View style={styles.listNumber}>
            <Text>
              {listNumber}.{counter++}
            </Text>
          </View>
          <View style={styles.beneficiaryInfoRow}>
            <Text>
              {t('citizenship.primary')}{' '}
              {withPlaceholder(getCountryName(beneficiary.primaryCitizenship?.country), 'country')},{' '}
              {withPlaceholder(
                getDocumentCategory(beneficiary.primaryCitizenship?.documentType),
                'docType',
              )}
              : {t('citizenship.number')}{' '}
              {withPlaceholder(beneficiary.primaryCitizenship?.number, 'number')},{' '}
              {t('citizenship.dateOfIssue')}{' '}
              {withPlaceholder(
                beneficiary.primaryCitizenship?.issueDate &&
                  dates.formatLong(beneficiary.primaryCitizenship?.issueDate),
                'date',
              )}
              .
            </Text>
          </View>
        </View>
      )}

      {beneficiary.otherCitizenships?.length && (
        <View style={{ ...styles.row, paddingLeft: 36 }} wrap={false}>
          <View style={styles.listNumber}>
            <Text>
              {listNumber}.{counter++}
            </Text>
          </View>
          <View style={styles.beneficiaryInfoRow}>
            <Text>
              {t('citizenship.other')}:{' '}
              {beneficiary.otherCitizenships?.map((citzenship, i, arr) => (
                <Fragment key={i}>
                  {withPlaceholder(getCountryName(citzenship?.country), 'country')},{' '}
                  {withPlaceholder(getDocumentCategory(citzenship?.documentType), 'docType')}:{' '}
                  {t('citizenship.number')} {withPlaceholder(citzenship?.number, 'number')},{' '}
                  {t('citizenship.dateOfIssue')}{' '}
                  {withPlaceholder(
                    citzenship?.issueDate && dates.formatLong(citzenship?.issueDate),
                    'date',
                  )}
                  {i === arr.length - 1 ? '.' : '; '}
                </Fragment>
              ))}
            </Text>
          </View>
        </View>
      )}

      {beneficiary.primaryResidence && (
        <View style={{ ...styles.row, paddingLeft: 36 }} wrap={false}>
          <View style={styles.listNumber}>
            <Text>
              {listNumber}.{counter++}
            </Text>
          </View>
          <View style={styles.beneficiaryInfoRow}>
            <Text>
              {t('residence.primary')}:{' '}
              {withPlaceholder(getResidenceAddress(beneficiary.primaryResidence), 'address')}
              {beneficiary.primaryResidence?.tax && (
                <>
                  , {t('residence.taxId')}: {beneficiary.primaryResidence?.tax}
                </>
              )}
              .
            </Text>
          </View>
        </View>
      )}

      {beneficiary.otherResidences?.length && (
        <View style={{ ...styles.row, paddingLeft: 36 }} wrap={false}>
          <View style={styles.listNumber}>
            <Text>
              {listNumber}.{counter++}
            </Text>
          </View>
          <View style={styles.beneficiaryInfoRow}>
            <Text>
              {t('residence.additional')}:{' '}
              {beneficiary.otherResidences?.map((residence, i, arr) => (
                <Fragment key={i}>
                  {withPlaceholder(getResidenceAddress(residence), 'address')}
                  {residence?.tax && (
                    <>
                      , {t('residence.taxId')}: {residence?.tax}
                    </>
                  )}
                  {i === arr.length - 1 ? '.' : '; '}
                </Fragment>
              ))}{' '}
            </Text>
          </View>
        </View>
      )}

      {(beneficiary.phoneNumber || beneficiary.email) && (
        <View style={{ ...styles.row, paddingLeft: 36 }} wrap={false}>
          <View style={styles.listNumber}>
            <Text>
              {listNumber}.{counter++}
            </Text>
          </View>
          <View style={styles.beneficiaryInfoRow}>
            <Text>
              {t('contacts.details')}:{' '}
              {beneficiary.phoneNumber && (
                <>
                  {beneficiary.phoneCode}
                  {beneficiary.phoneNumber}
                  {beneficiary.email && ', '}
                </>
              )}
              {beneficiary.email && <>{beneficiary.email}</>}.
            </Text>
          </View>
        </View>
      )}

      {beneficiary.occupation && (
        <View style={{ ...styles.row, paddingLeft: 36 }} wrap={false}>
          <View style={styles.listNumber}>
            <Text>
              {listNumber}.{counter++}
            </Text>
          </View>
          <View style={styles.beneficiaryInfoRow}>
            <Text>
              {t('occupation')}: {beneficiary.occupation}.
            </Text>
          </View>
        </View>
      )}

      <View
        style={{
          paddingLeft: 36,
        }}
      >
        <Text>
          {t(`${block}.refferedAs`, { fullName: beneficiary.firstName, index: index + 1 })}
        </Text>
        {spouse === beneficiary.id && (
          <Text>
            {t(`${block}.spouse`, { firstName: beneficiary.firstName, index: index + 1 })}
          </Text>
        )}
      </View>
    </View>
  );
};
